/*
 * This file contains utility functions that can be used across the application.
 */

/*
 * This function rounds a number to the specified number of decimal places.
 * @param {number} number - The number to round.
 * @param {number} [decimalPlaces = 2] - The number of decimal places to round to.
 * @returns {number} The rounded number.
 * @example
 * roundNumber(1.2345); // 1.23
 * roundNumber(1.2345, 1); // 1.2
 * roundNumber(1.2345, 3); // 1.235
 */
export const roundNumber = (number, decimalPlaces = 2) =>
	Math.round(parseFloat((number * Math.pow(10, 2)).toFixed(decimalPlaces))) / Math.pow(10, 2);

/*
 * This function validates an IBAN (International Bank Account Number) by verifying its structure and checking the checksum.
 * @param {string} userIban - The IBAN to validate.
 * @returns {boolean} True if the IBAN is valid, otherwise false.
 * @example
 * validateIBAN('SA2765000000101556585011'); // true
 * validateIBAN('SA2865000000101556585011'); // false
 */
export const validateIBAN = (userIban) => {
	const iban = userIban.replace(/\s+/g, '').toUpperCase();

	const rearrangedIBAN = iban.slice(4) + iban.slice(0, 4);

	let numericIBAN = '';
	for (let i = 0; i < rearrangedIBAN.length; i += 1) {
		const char = rearrangedIBAN.charAt(i);
		if (char >= 'A' && char <= 'Z') {
			numericIBAN += (char.charCodeAt(0) - 55).toString();
		} else {
			numericIBAN += char;
		}
	}

	let remainder = parseInt(numericIBAN.slice(0, 2), 10);
	for (let i = 2; i < numericIBAN.length; i += 1) {
		remainder = (remainder * 10 + parseInt(numericIBAN.charAt(i), 10)) % 97;
	}

	return remainder === 1;
};

/*
 * This function checks if an IBAN (International Bank Account Number) has a valid format.
 * @param {string} userIban - The IBAN to check.
 * @returns {boolean} True if the IBAN format is valid, otherwise false.
 * @example
 * validateIBANFormat('SA2765000000101556585011'); // true
 * validateIBANFormat('SA2765000 000101556 585011 '); // false
 */
export const validateIBANFormat = (userIban) => {
	const ibanRegex =
		/^(?:(?:CR|DE|ME|RS|VA)\d{20}|(?:CZ|ES|SE|SK|TN)\d{22}|(?:DK|FI|FO|GL|SD)\d{16}|(?:AT|BA|EE|LT|XK)\d{18}|(?:AE|IL|TL)\d{21}|(?:LY|PT|ST)\d{23}|(?:IT|SM)\d{2}[A-Z]\d{10}[A-Za-z0-9]{12}|(?:HU|PL)\d{26}|(?:AL|CY)\d{10}[A-Za-z0-9]{16}|(?:CH|LI)\d{7}[A-Za-z0-9]{12}|(?:FR|MC)\d{12}[A-Za-z0-9]{11}\d{2}|(?:GB|IE)\d{2}[A-Z]{4}\d{14}|(?:KZ|LU)\d{5}[A-Za-z0-9]{13}|(?:GI|IQ)\d{2}[A-Z]{4}[A-Za-z0-9]{15}|(?:PK|RO)\d{2}[A-Z]{4}[A-Za-z0-9]{16}|(?:PS|QA)\d{2}[A-Z]{4}[A-Za-z0-9]{21}|AD\d{10}[A-Za-z0-9]{12}|AZ\d{2}[A-Z]{4}[A-Za-z0-9]{20}|BE\d{14}|BG\d{2}[A-Z]{4}\d{6}[A-Za-z0-9]{8}|BH\d{2}[A-Z]{4}[A-Za-z0-9]{14}|BR\d{25}[A-Z][A-Za-z0-9]|BY\d{2}[A-Za-z0-9]{4}\d{4}[A-Za-z0-9]{16}|DO\d{2}[A-Za-z0-9]{4}\d{20}|EG\d{27}|GE\d{2}[A-Z]\d{16}|GT\d{2}[A-Za-z0-9]{24}|GR\d{9}[A-Za-z0-9]{16}|HR\d{19}|IS\d{24}|JO\d{2}[A-Z]{4}\d{4}[A-Za-z0-9]{18}|KW\d{2}[A-Z]{4}[A-Za-z0-9]{22}|LC\d{2}[A-Z]{4}[A-Za-z0-9]{24}|LB\d{6}[A-Za-z0-9]{20}|LV\d{2}[A-Z]{4}\d{13}|MD\d{2}[A-Za-z0-9]{20}|MK\d{5}[A-Za-z0-9]{10}\d{2}|MR\d{25}|MT\d{2}[A-Z]{4}\d{5}[A-Za-z0-9]{18}|MU\d{2}[A-Z]{4}\d{19}[A-Z]{3}|NL\d{2}[A-Z]{4}\d{10}|NO\d{13}|SA\d{4}[A-Za-z0-9]{18}|SC\d{2}[A-Z]{4}\d{20}[A-Z]{3}|SI\d{17}|SV\d{2}[A-Z]{4}\d{20}|TR\d{8}[A-Za-z0-9]{16}|UA\d{8}[A-Za-z0-9]{19}|VG\d{2}[A-Z]{4}\d{16}|GE\d{2}[A-Z]{2}\d{16})$/;

	return ibanRegex.test(userIban);
};

export const formatPrice = (value, langCode) =>
	new Intl.NumberFormat(langCode.replace('_', '-'), {
		useGrouping: true,
		numberingSystem: 'latn'
	}).format(value);
